import { Dropdown, Menu, Radio } from 'antd';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  ConfirmModal,
  IncludeTemplateIconFull as unmarkIncludeIcon,
  IncludeTemplateIconOutLine as markIncludeIcon,
  EcgToolbarArrowIcon as ToolbarArrowIcon,
  ReviewedIcon16 as reviewMarkIcon,
  UnreviewedIcon as reviewUnmarkIcon,
} from '@/shared/ui';
import { TEMPLATE_SOURCE_OPTIONS } from '@/shared/config';
import useTemplateAction from './useTemplateAction';
import './styles.scss';

/**
 * Toolbar above the beat template overview offering the source filter and the review and include-in-report actions. Pulls its state from useTemplateAction, always shows the source dropdown, reveals the mark-reviewed and mark-included icon buttons with tooltips only while the source is ALL, disables them without a selected template, and renders the confirm modal warning that at most five morphologies fit in the report.
 *
 * @returns The template toolbar with its source dropdown, action buttons and the include-limit modal.
 */
const TemplateAction = () => {
  const {
    viewBeatsSource,
    selectedTemplate,
    activeButton,
    state,
    setState,
    onChangeFilter,
    onClickMarkReview,
    onClickMarkInclude,
    isReviewed,
    isIncludeReport,
  } = useTemplateAction();

  /**
   * Renders the template source picker for the beat template overview. Maps TEMPLATE_SOURCE_OPTIONS into radio menu items showing each option's label and description, checks the one matching the current viewBeatsSource, and hangs the menu off a click-triggered dropdown button.
   *
   * @returns The source dropdown button with its radio menu.
   */
  const renderFilterDropdown = () => {
    const items: any[] = [];
    _.forEach(TEMPLATE_SOURCE_OPTIONS, (x, i) =>
      items.push({
        key: `template-filter-${i}`,
        label: (
          <Radio
            key={i}
            checked={x.value === viewBeatsSource.value}
            value={x.value}
            className="bulk-action-radio"
            id={`beat-bulk-action-${i}`}
          >
            <div className="source-menu-option">
              <div className="source-menu-option__label">{x.label}</div>
              <div className="source-menu-option__desc">{x.desc}</div>
            </div>
          </Radio>
        ),
        value: x.value,
      }),
    );
    return (
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu
            onClick={onChangeFilter}
            className="thumbnail-size-menu --source-menu"
            items={items}
          />
        }
        placement="bottomLeft"
      >
        <button id="beats-template-source-dropdown-btn" className="thumbnail-size-text">
          <span>{viewBeatsSource?.label}</span>
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div>
      <div className="rr-template-button-action-container">
        <div className="beat-bulk-action-container template-source-action">
          <div className="beat-action-title">Source</div>
          <div className="beat-bulk-action">{renderFilterDropdown()}</div>
        </div>
        {viewBeatsSource.value === 'ALL' && (
          <div className="beat-bulk-action-container">
            <div className="beat-action-title">Tools</div>
            <div className="beat-bulk-action">
              <div className="template-action-button">
                <IconButton
                  id="btnMarkReviewBeatTemplate"
                  isOutline
                  iconComponent={
                    <img
                      src={isReviewed ? reviewMarkIcon : reviewUnmarkIcon}
                      alt="Review mark icon"
                    />
                  }
                  disabled={_.isEmpty(selectedTemplate)}
                  onClick={() => onClickMarkReview(!isReviewed)}
                />
                <UncontrolledTooltip
                  className="custom-tooltip max-width-200"
                  target="btnMarkReviewBeatTemplate"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                  trigger="hover"
                >
                  <div className="beat-tooltip">
                    {isReviewed ? (
                      <div className="beat-tooltip">Mark as not reviewed</div>
                    ) : (
                      <div className="beat-tooltip">Mark as reviewed</div>
                    )}
                  </div>
                </UncontrolledTooltip>
                {/* TBeatHrActiveTab (Task 7 model.ts) is a numeric enum, so 'V' the string literal never overlaps its member type - pre-existing gap, not fixed here. */}
                {(activeButton as any) === 'V' && (
                  <>
                    <IconButton
                      id="btnMarkIncludeBeatTemplate"
                      isOutline
                      iconComponent={
                        <img
                          src={isIncludeReport ? unmarkIncludeIcon : markIncludeIcon}
                          alt="Review mark icon"
                        />
                      }
                      disabled={_.isEmpty(selectedTemplate) || selectedTemplate?.unclassified}
                      onClick={() => onClickMarkInclude(!isIncludeReport)}
                    />
                    <UncontrolledTooltip
                      className="custom-tooltip max-width-200"
                      target="btnMarkIncludeBeatTemplate"
                      placement="bottom"
                      delay={{ show: 0, hide: 0 }}
                      hideArrow
                      trigger="hover"
                    >
                      <div className="beat-tooltip">
                        {selectedTemplate?.unclassified
                          ? 'This action is not available for unclassified morphologies'
                          : isIncludeReport
                            ? 'Exclude in report'
                            : 'Include in report'}
                      </div>
                    </UncontrolledTooltip>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={state.isOpenIncludeModal}
        onClickRightButton={() => {
          setState({ isOpenIncludeModal: false });
        }}
        rightButtonName="Okay"
        title="Cannot include morphology"
        question="A maximum of 5 morphologies can be included in the report. Please exclude others and try again."
        isRedBtn={false}
        isFilledRightBtn={false}
        className="template-beats-modal modal-width-350"
      />
    </div>
  );
};

export default TemplateAction;
